// The server publishes the dashboard payload only to connections in its dashboard group. Group membership
// belongs to the connection, and every tab shares the worker's one connection, so the worker joins while
// any tab has a page open that shows the payload, and leaves when the last one unsubscribes or goes away.

export type DashboardHubMethod = "SubscribeDashboard" | "UnsubscribeDashboard";

/**
 * The data hub's URL, marked so the server knows this client subscribes for itself. The server puts a
 * connection without the mark in the dashboard group on connect, which keeps tabs still running a bundle
 * from before subscriptions updating. The mark is in the URL so a reconnect carries it too.
 */
export const dataHubUrl = (serverURL: string | undefined): string => `${serverURL}/data-hub?dashboardSubscribe=1`;

export interface DashboardHub {
    isConnected(): boolean;
    invoke(method: DashboardHubMethod): Promise<unknown>;
}

export interface DashboardSubscriptions<Port> {
    /** Records that this port has a page open that shows the payload. */
    add(port: Port): Promise<void>;
    /** Records that this port no longer does, or has gone away. */
    remove(port: Port): Promise<void>;
    /** The connection started or reconnected, so the server holds no group membership for it. */
    connectionReset(): Promise<void>;
    /** True once the server has confirmed this connection is in the group. */
    readonly subscribedOnServer: boolean;
}

// `getHub` is read on every sync rather than captured once: the worker replaces its connection after a
// failed start, and a captured one would keep invoking on the dead connection.
export function createDashboardSubscriptions<Port>(getHub: () => DashboardHub | undefined): DashboardSubscriptions<Port> {
    const ports = new Set<Port>();
    let subscribedOnServer = false;

    // Chained so that a subscribe and an unsubscribe in quick succession reach the server in order, and
    // each step decides from the state at the time it runs rather than the state when it was queued.
    let pending: Promise<void> = Promise.resolve();

    const sync = (): Promise<void> => {
        pending = pending.then(async () => {
            const wanted = ports.size > 0;
            const hub = getHub();
            if (!hub?.isConnected() || wanted === subscribedOnServer) return;

            const method: DashboardHubMethod = wanted ? "SubscribeDashboard" : "UnsubscribeDashboard";
            try {
                await hub.invoke(method);
                subscribedOnServer = wanted;
            } catch (err) {
                // An API from before these methods existed publishes to every connection, so the page still
                // updates. Left unconfirmed, the next add, remove or reconnect tries again.
                console.warn(`SharedWorker: ${method} failed`, err);
            }
        });
        return pending;
    };

    return {
        add(port) {
            ports.add(port);
            return sync();
        },
        remove(port) {
            return ports.delete(port) ? sync() : pending;
        },
        connectionReset() {
            subscribedOnServer = false;
            return sync();
        },
        get subscribedOnServer() {
            return subscribedOnServer;
        },
    };
}
